import { useCallback, useEffect, useState } from 'react'
import type { ClientModelOption } from '../client-config'

// dev 的模型切換（#1726）。選單顯示「這條對話最新一則套用的模型」；新對話才用本機記住的。
// 本機記的是「上次送出時用的」，由送出那一刻寫入（見 ChatPage），切換本身不寫。
// 🔴 本機儲存只是個人便利：讀寫失敗（無痕、被封鎖）一律當沒有，聊天照常。

const STORAGE_KEY = 'ai-chat:model'
// 選單上的「{模型}-debug」是前端假選項：送出的 model 不變，只打開偵錯元素
const DEBUG_STORAGE_KEY = 'ai-chat:debug'
const DEBUG_SUFFIX = '::debug'

export const readStoredModel = (): string | null => {
  try {
    return window.localStorage.getItem(STORAGE_KEY)
  } catch {
    return null
  }
}

export const storeModel = (modelId: string): void => {
  try {
    window.localStorage.setItem(STORAGE_KEY, modelId)
  } catch {
    // 寫不進去就只在這次頁面有效，不影響送出
  }
}

export const readStoredDebug = (): boolean => {
  try {
    return window.localStorage.getItem(DEBUG_STORAGE_KEY) === 'true'
  } catch {
    return false
  }
}

export const storeDebug = (debug: boolean): void => {
  try {
    window.localStorage.setItem(DEBUG_STORAGE_KEY, String(debug))
  } catch {
    // 同 storeModel：只在這次頁面有效
  }
}

// 選單的 option value：同一個模型的一般與 -debug 兩項要分得開，送出前再拆回模型 id
export const toChoiceValue = (modelId: string, debug: boolean): string =>
  (debug ? `${modelId}${DEBUG_SUFFIX}` : modelId)

export const parseChoiceValue = (value: string): { model: string; debug: boolean } =>
  (value.endsWith(DEBUG_SUFFIX) ?
    { model: value.slice(0, -DEBUG_SUFFIX.length), debug: true } : { model: value, debug: false })

// 優先序：這條對話最新一則用的 → 本機記住的 → 預設 → 清單第一筆。不在清單的一律跳過。
// 🔴 切了沒送出就重新整理，回到的是對話實際用的那個，不是本機剛切的值
export const pickInitialModel = (
  conversationModel: string | null,
  stored: string | null,
  options: ClientModelOption[],
  defaultModel: string | null,
): string | null => {
  const inList = (id: string | null): id is string =>
    !!id && options.some((option) => option.id === id)
  return [conversationModel, stored, defaultModel].find(inList) ?? options[0]?.id ?? null
}

// 沒有清單（非 dev 或設定還沒讀回）時 model 是 null、debug 是 false，送出時不帶模型。
// 對話模型讀回來時重新決定一次；debug 不隨對話記，一律取本機記住的
export const useModelChoice = (
  options: ClientModelOption[], defaultModel: string | null, conversationModel: string | null,
) => {
  const [model, setModel] = useState<string | null>(null)
  const [debug, setDebug] = useState(false)
  useEffect(() => {
    setModel(options.length ?
      pickInitialModel(conversationModel, readStoredModel(), options, defaultModel) : null)
    setDebug(options.length > 0 && readStoredDebug())
  }, [options, defaultModel, conversationModel])
  // 只換畫面上的選擇；寫本機要等送出（切了沒送出不算）
  const choose = useCallback((modelId: string, nextDebug = false) => {
    setModel(modelId)
    setDebug(nextDebug)
  }, [])
  return { model, debug, choose }
}
